import { Activity, CheckCircle2, ClipboardList, Clock } from "lucide-react";
import { KpiCard } from "@/components/dashboard/KpiCard";
import { TechPartnerAuditsTable } from "@/components/dashboard/TechPartnerAuditsTable";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { useTechPartnerDashboard } from "@/hooks/use-tech-partner-dashboard";

export function TechPartnerDashboardPage() {
  const { data, isLoading, isError, error, refetch } = useTechPartnerDashboard();
  const currentYear = new Date().getFullYear();

  if (isError) return <ErrorState message={error.message} onRetry={refetch} />;

  const metrics = data?.metrics;
  const show = (value: number | undefined, suffix = "") =>
    isLoading || value === undefined ? "—" : `${value}${suffix}`;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Adherence Dashboard"
        description="Track adherence across the projects you lead as Tech Partner."
      />
      <div className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Adherence Metrics
        </p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard
            title="Overall Percentage Completed"
            value={show(metrics?.passRate, "%")}
            progress={metrics?.passRate}
            icon={Activity}
            iconBg="bg-emerald-500/10"
            iconColor="text-emerald-600 dark:text-emerald-400"
          />
          <KpiCard
            title={`My Projects in Scope for ${currentYear}`}
            value={show(metrics?.totalInScope)}
            icon={ClipboardList}
            iconBg="bg-slate-500/10"
            iconColor="text-slate-600 dark:text-slate-400"
          />
          <KpiCard
            title="Completed Audits"
            value={show(metrics?.completed)}
            icon={CheckCircle2}
            iconBg="bg-emerald-500/10"
            iconColor="text-emerald-600 dark:text-emerald-400"
          />
          <KpiCard
            title="Remaining Projects"
            value={show(metrics?.remainingProjects)}
            icon={Clock}
            iconBg="bg-blue-500/10"
            iconColor="text-blue-600 dark:text-blue-400"
          />
        </div>
      </div>
      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <TechPartnerAuditsTable audits={data?.audits ?? []} />
      )}
    </div>
  );
}
